import {
  createContext,
  forwardRef,
  useId,
  ComponentPropsWithoutRef,
  useRef,
} from "react";
import cx from "classnames";
import styles from "./SegmentedControl.module.scss";
import {
  SegmentedControlSegment,
  SegmentedControlSegmentProps,
} from "./SegmentedControlSegment";
import { LayoutUtilProps } from "../Label";
import { Size } from "../../types";
import { useMergeRefs, useOptionallyControlledState } from "../../hooks";
import { motion } from "motion/react";

/**
 * Context props for SegmentedControl internal state management
 */
export type SegmentedControlContextProps = {
  /**
   * Currently selected segment value
   */
  current?: SegmentedControlSegmentProps["value"];
  /**
   * Function to set the current segment
   */
  setCurrent?: (value: SegmentedControlSegmentProps["value"]) => void;
  /**
   * Change handler for the segmented control
   */
  onChange?: SegmentedControlProps["onChange"];
  /**
   * Name attribute for radio inputs
   */
  name?: string;
};

/**
 * Base props for SegmentedControl component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag" | "onChange">
 * @extends LayoutUtilProps
 */
type BaseSegmentedControlProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag" | "onChange"
> &
  LayoutUtilProps & {
    /**
     * Callback when segment is changed
     */
    onChange?: (value: SegmentedControlSegmentProps["value"]) => void;

    /**
     * Size of the segment
     * @default "medium"
     */
    size?: Extract<Size, "small" | "medium">;

    /**
     * Whether to fill available width
     * @default false
     */
    fill?: boolean;

    /**
     * Name attribute for radio inputs
     * @remarks If empty, it will auto generate one using useId()
     */
    name?: string;

    /**
     * The segments to render within the segmented control
     */
    children?: React.ReactNode;
  };

/**
 * Props for controlled SegmentedControl
 */
type ControlledSegmentedControlProps = BaseSegmentedControlProps & {
  /**
   * Selected segment (controlled mode)
   */
  selected: SegmentedControlSegmentProps["value"];
  defaultSelected?: never;
};

/**
 * Props for uncontrolled SegmentedControl
 */
type UncontrolledSegmentedControlProps = BaseSegmentedControlProps & {
  /**
   * Initial selection (uncontrolled mode)
   */
  defaultSelected: SegmentedControlSegmentProps["value"];
  selected?: never;
};

/**
 * Props for the SegmentedControl component
 */
export type SegmentedControlProps =
  | ControlledSegmentedControlProps
  | UncontrolledSegmentedControlProps;

export const SegmentedControlContext =
  createContext<SegmentedControlContextProps>({});

const SegmentedControlElement = forwardRef<
  HTMLDivElement,
  SegmentedControlProps
>((props, ref) => {
  const {
    className,
    onClick,
    onChange,
    children,
    size = "medium",
    defaultSelected,
    selected,
    name,
    fill,
    ...rest
  } = props;

  const [current, setCurrent] = useOptionallyControlledState({
    controlledValue: selected,
    defaultValue: defaultSelected,
    onChange,
  });

  const segmentedControlRef = useRef<HTMLDivElement>(null);

  const SegmentedControlClassNames = cx(
    styles["segmented-control"],
    styles[size],
    className,
    {
      [styles.fill]: fill,
    },
  );

  const uid = useId();

  return (
    <motion.div
      transition={{ duration: 0 }}
      layout
      role="radiogroup"
      className={SegmentedControlClassNames}
      data-anv="segmented-control"
      ref={useMergeRefs([segmentedControlRef, ref])}
      {...rest}
    >
      <SegmentedControlContext.Provider
        value={{ current, setCurrent, name: name ?? uid }}
      >
        {children}
      </SegmentedControlContext.Provider>
    </motion.div>
  );
});

SegmentedControlElement.displayName = "SegmentedControl";

/**
 * SegmentedControl component for selecting one option from a group of related choices.
 *
 * Features:
 * - Supports both controlled and uncontrolled modes
 * - Multiple size variants (small, medium)
 * - Optional fill width mode
 * - Smooth animations with motion
 * - Accessibility support with proper radio group semantics
 * - Layout utility props for positioning and spacing
 * - Context-based state management
 * - Auto-generated or custom name attributes
 * - Segment support via SegmentedControl.Segment
 *
 * @example
 * <SegmentedControl
 *   selected={selectedValue}
 *   onChange={setSelectedValue}
 *   size="medium"
 * >
 *   <SegmentedControl.Segment value="option1">Option 1</SegmentedControl.Segment>
 *   <SegmentedControl.Segment value="option2">Option 2</SegmentedControl.Segment>
 *   <SegmentedControl.Segment value="option3">Option 3</SegmentedControl.Segment>
 * </SegmentedControl>
 *
 * @example
 * <SegmentedControl
 *   defaultSelected="small"
 *   size="small"
 *   fill
 * >
 *   <SegmentedControl.Segment value="small" icon={SmallIcon}>Small</SegmentedControl.Segment>
 *   <SegmentedControl.Segment value="large" icon={LargeIcon}>Large</SegmentedControl.Segment>
 * </SegmentedControl>
 */
export const SegmentedControl = Object.assign(SegmentedControlElement, {
  /**
   * SegmentedControlSegment component for individual segments within a segmented control.
   *
   * Features:
   * - Individual segment with radio input
   * - Optional icon support
   * - Animated selection indicator
   * - Accessibility support with proper ARIA attributes
   * - Context integration with parent SegmentedControl
   * - Motion animations with reduced motion support
   *
   * @example
   * <SegmentedControl.Segment value="option1" icon={Icon1}>
   *   Option 1
   * </SegmentedControl.Segment>
   */
  Segment: SegmentedControlSegment,
});

export default SegmentedControl;
